import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';

import { eventsApi } from '@/features/events/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { useCurrentCompany } from '@/lib/branding/useCurrentBrand';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';

/** Cuánto dura «recién llegado». */
const DIAS_DE_GRACIA = 7;

/**
 * La guía de los primeros días: qué toca ahora.
 *
 * Subir el merch es el primer paso natural y también el más engañoso: al
 * terminar, el catálogo se ve lleno y la aplicación parece hecha — pero nada de
 * eso está en la calle todavía. Falta el proyecto que agrupa la campaña y el
 * evento donde se reparte. Quien no lo sabe se queda mirando su catálogo
 * esperando que pase algo.
 *
 * **Se apaga sola.** Sólo aparece durante los primeros siete días de la cuenta,
 * y desaparece en cuanto los pasos están hechos. Una guía que sigue ahí en el
 * mes cuarto deja de leerse y pasa a estorbar, que es como mueren los onboarding
 * permanentes.
 *
 * El paso del merch se marca hecho con lo que la pantalla ya sabe (`tieneMerch`
 * lo pasa quien la pinta), así que no cuesta ninguna consulta extra.
 */
export function NextStepsGuide({
  brandId,
  tieneMerch,
}: {
  brandId: number | null;
  /** ¿Hay algo en el catálogo? Lo sabe la pantalla que ya cargó la lista. */
  tieneMerch: boolean;
}) {
  const t = useT();
  const { company } = useCurrentCompany();

  const reciente = esCuentaNueva(company?.createdAt);

  // Sólo se pregunta si la cuenta es nueva: en una cuenta veterana esta guía no
  // se va a pintar pase lo que pase, y dos consultas para no enseñar nada son
  // dos consultas de más en cada visita al catálogo.
  const proyectosQ = useQuery({
    queryKey: queryKeys.eventGroups.byBrand(brandId ?? 0),
    queryFn: () => eventsApi.listGroups({ companyId: brandId }),
    enabled: reciente && !!brandId,
  });
  const eventosQ = useQuery({
    queryKey: queryKeys.events.byBrand(brandId ?? 0),
    queryFn: () => eventsApi.listEvents({ companyId: brandId }),
    enabled: reciente && !!brandId,
  });

  if (!reciente) return null;
  // Mientras no se sepa, no se pinta: una guía que aparece a medio cargar y se
  // desvanece a los dos segundos se lee como un fallo.
  if (proyectosQ.isLoading || eventosQ.isLoading) return null;

  const tieneProyecto = (proyectosQ.data?.items?.length ?? 0) > 0;
  const tieneEvento = (eventosQ.data?.items?.length ?? 0) > 0;
  if (tieneMerch && tieneProyecto && tieneEvento) return null;

  const pasos = [
    {
      clave: 'merch',
      hecho: tieneMerch,
      titulo: t('nextSteps.merch'),
      nota: t('nextSteps.merchHint'),
      to: null,
    },
    {
      clave: 'project',
      hecho: tieneProyecto,
      titulo: t('nextSteps.project'),
      nota: t('nextSteps.projectHint'),
      to: brandId ? `/brand/${brandId}/projects` : null,
    },
    {
      clave: 'event',
      hecho: tieneEvento,
      titulo: t('nextSteps.event'),
      nota: t('nextSteps.eventHint'),
      // Sin proyecto no hay dónde colgar un evento, así que hasta entonces el
      // paso se enseña pero no lleva a ninguna parte.
      to: tieneProyecto && brandId ? `/brand/${brandId}/projects` : null,
    },
  ];
  const siguiente = pasos.find((p) => !p.hecho);

  return (
    <section
      data-tour="next-steps"
      className="mt-8 rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-5"
    >
      <header className="mb-4">
        <h2 className="text-sm font-extrabold uppercase tracking-widest text-[var(--color-xk-lavender)]">
          {t('nextSteps.title')}
        </h2>
        <p className="mt-1 text-[12px] text-[var(--color-xk-text-muted)]">
          {t('nextSteps.subtitle')}
        </p>
      </header>

      <ol className="grid gap-2 sm:grid-cols-3">
        {pasos.map((paso, i) => {
          const esElSiguiente = paso.clave === siguiente?.clave;
          const cuerpo = (
            <>
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'flex h-5 w-5 flex-none items-center justify-center rounded-full text-[10px] font-bold',
                    paso.hecho
                      ? 'bg-[var(--color-xk-accent)] text-[var(--color-fg-on-accent)]'
                      : 'border border-[var(--color-xk-border)] text-[var(--color-xk-text-muted)]',
                  )}
                >
                  {paso.hecho ? <Check className="h-3 w-3" /> : i + 1}
                </span>
                <span
                  className={cn(
                    'text-[12px] font-bold',
                    paso.hecho
                      ? 'text-[var(--color-xk-text-muted)] line-through'
                      : 'text-[var(--color-xk-lavender)]',
                  )}
                >
                  {paso.titulo}
                </span>
                {esElSiguiente && paso.to && (
                  <ArrowRight className="ml-auto h-3.5 w-3.5 text-[var(--color-xk-accent)]" />
                )}
              </div>
              {!paso.hecho && (
                <p className="mt-1.5 pl-7 text-[11px] leading-snug text-[var(--color-xk-text-muted)]">
                  {paso.nota}
                </p>
              )}
            </>
          );

          const clases = cn(
            'block rounded-xl border p-3 transition-colors',
            esElSiguiente
              ? 'border-[var(--color-xk-accent)] bg-[var(--color-xk-bg-deep)]'
              : 'border-[var(--color-xk-border)]',
          );

          return (
            <li key={paso.clave}>
              {paso.to ? (
                <Link to={paso.to} className={cn(clases, 'hover:border-[var(--color-accent-ink)]')}>
                  {cuerpo}
                </Link>
              ) : (
                <div className={clases}>{cuerpo}</div>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/**
 * ¿La cuenta tiene menos de una semana?
 *
 * Sin fecha se responde que NO: es preferible no enseñar la guía a enseñársela
 * para siempre a una cuenta de hace dos años porque un campo llegó vacío.
 */
function esCuentaNueva(createdAt: string | undefined | null): boolean {
  if (!createdAt) return false;
  const alta = new Date(createdAt).getTime();
  if (Number.isNaN(alta)) return false;
  return Date.now() - alta < DIAS_DE_GRACIA * 24 * 60 * 60 * 1000;
}
